import { useLayoutEffect, useRef } from "react";
import type { ComponentProps } from "react";

interface AmountInputProps
  extends Omit<
    ComponentProps<"input">,
    "value" | "onChange" | "type" | "inputMode"
  > {
  /** Raw, unformatted amount, e.g. "5000.5". */
  value: string;
  onChange: (raw: string) => void;
  allowNegative?: boolean;
}

/** "5000.5" -> "5,000.5"; keeps a trailing "." and typed decimals. */
function formatAmountInput(raw: string): string {
  const negative = raw.startsWith("-");
  const body = negative ? raw.slice(1) : raw;
  const [int, dec] = body.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}${dec === undefined ? "" : `.${dec}`}`;
}

function countRawChars(text: string, end: number): number {
  return text.slice(0, end).replace(/,/g, "").length;
}

/** Text input that shows thousands separators live and emits the raw string. */
export default function AmountInput({
  value,
  onChange,
  allowNegative = false,
  className = "",
  ...rest
}: AmountInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const caretRaw = useRef<number | null>(null);
  const display = formatAmountInput(value);
  const pattern = allowNegative ? /^-?\d*\.?\d{0,2}$/ : /^\d*\.?\d{0,2}$/;

  // After reformatting, put the caret after the same number of raw chars.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || caretRaw.current === null) return;
    let pos = 0;
    let seen = 0;
    while (pos < display.length && seen < caretRaw.current) {
      if (display[pos] !== ",") seen++;
      pos++;
    }
    el.setSelectionRange(pos, pos);
    caretRaw.current = null;
  }, [display]);

  return (
    <input
      {...rest}
      ref={ref}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={(e) => {
        const el = e.target;
        const next = el.value.replace(/,/g, "");
        if (!pattern.test(next)) return;
        caretRaw.current = countRawChars(el.value, el.selectionStart ?? 0);
        onChange(next);
      }}
      className={`[font-variant-numeric:tabular-nums] ${className}`}
    />
  );
}
